<template>
  <div class="plan-heading">
    <div class="header-1">{{ $t('plans.choose') }}</div>

    <div class="toggle-container">
      <button
        class="toggle-option"
        :class="{ active: !isAnnual }"
        @click="setAnnual(false)"
      >
        {{ $t('plans.monthly') }}
      </button>

      <button
        class="toggle-option"
        :class="{ active: isAnnual }"
        @click="setAnnual(true)"
      >
        {{ $t('plans.annual') }}
      </button>
    </div>
  </div>

  <div class="plan-content">
    <PlanCard
      v-for="(plan, index) in plansData"
      :key="index"
      class="plan-card"
      :planTitle="plan.title"
      :planDescription="plan.description"
      :priceMonthly="plan.priceMonthly"
      :priceAnnual="plan.priceAnnual"
      :features="plan.features"
      :isAnnual="isAnnual"
      :btnLabel="getBtnLabel(plan)"
      :isActive="isActivePlan(plan.paypalPlanId)"
      :isRecommended="plan.title === 'Pro'"
      @selectPlan="redirectToCheckout"
    />
  </div>

  <div class="plan-content-description description gray-text">
    {{ $t('plans.creditsNote') }}
  </div>
</template>

<script setup>
import PlanCard from "@/components/PlanCard.vue";
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { plans } from "@/constants/plans";
import { trackEvent } from "@/utils/analytics";
import { useI18n } from 'vue-i18n';
import { localizePlan } from '@/i18n/planCatalog';

const router = useRouter();
const store = useStore();
const { t, tm } = useI18n();

const isAnnual = ref(false);
const isLoading = ref(true);
const apiPlans = ref(null);

const currentPlan = computed(() => store.state.activePlan || "Freemium");

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch("fetchUserDetails"),
      store.dispatch("fetchPlanDetails"),
    ]);

    trackEvent("plans_page_viewed", {
      current_plan: store.state.activePlan || "Freemium",
    });

    const planDetailsFromAPI = store.getters.getPlanDetails;
    if (planDetailsFromAPI) {
      apiPlans.value = planDetailsFromAPI;
      updatePlansData();
    } else {
      // Fallback to local plans if API fails
      console.log("Using local plans as fallback");
    }
  } catch (error) {
    console.error("Failed to fetch data:", error);
  } finally {
    isLoading.value = false;
  }
});

function isActivePlan(planPaypalId) {
  return currentPlan.value === planPaypalId[isAnnual.value ? "annual" : "monthly"];
}

function getBtnLabel(plan) {
  if (plan.title == "Freemium") {
    return t('plans.freemium');
  }

  if (isActivePlan(plan.paypalPlanId)) {
    return t('plans.current');
  }

  if (plan.title === "Enterprise") {
    return t('plans.contactSales');
  }

  if (plan.btnLabel) {
    return plan.btnLabel;
  }

  return t('plans.select');
}

function setAnnual(value) {
  isAnnual.value = value;
  trackEvent("billing_cycle_toggled", {
    billing_cycle: value ? "annual" : "monthly",
    current_plan: currentPlan.value,
  });
}

const rawPlansData = ref([
  {
    ...plans.Freemium,
    paypalPlanId: plans.Freemium.paypalPlanId,
  },
  {
    ...plans.Basic,
    paypalPlanId: {
      monthly: "Monthly Standard",
      annual: "Annual Standard",
    },
  },
  {
    ...plans.Pro,
    paypalPlanId: {
      monthly: "Monthly Pro",
      annual: "Annual Pro",
    },
  },
  {
    ...plans.Enterprise,
    paypalPlanId: plans.Enterprise.paypalPlanId,
  },
]);

const plansData = computed(() => rawPlansData.value.map((plan) => localizePlan(plan, t, tm)));

function updatePlansData() {
  if (!apiPlans.value) return;

  // TODO(i18n): API currently returns a single-language catalog. Keep its prices
  // and plan IDs, while the locale JSON supplies temporary localized copy. When
  // the backend accepts a locale and returns localized descriptions/features,
  // preserve those fields here and use planCatalog only as a fallback.
  // Create a new array to store updated plans
  const updatedPlans = [];

  // Map API plan data to our format
  for (const [key, plan] of Object.entries(apiPlans.value)) {
    if (key === "Freemium") {
      updatedPlans.push({
        title: plan.title,
        description: plan.description,
        priceMonthly: plan.price_monthly,
        priceAnnual: plan.price_annual,
        creditsByDefault: plan.credits_by_default,
        features: plan.features,
        paypalPlanId: {
          monthly: "Freemium",
          annual: "Freemium",
        },
      });
    } else if (key === "Basic") {
      updatedPlans.push({
        title: plan.title,
        description: plan.description,
        priceMonthly: plan.price_monthly,
        priceAnnual: plan.price_annual,
        creditsByDefault: plan.credits_by_default,
        features: plan.features,
        paypalPlanId: {
          monthly: "Monthly Standard",
          annual: "Annual Standard",
        },
      });
    } else if (key === "Pro") {
      updatedPlans.push({
        title: plan.title,
        description: plan.description,
        priceMonthly: plan.price_monthly,
        priceAnnual: plan.price_annual,
        creditsByDefault: plan.credits_by_default,
        features: plan.features,
        paypalPlanId: {
          monthly: "Monthly Pro",
          annual: "Annual Pro",
        },
      });
    } else if (key === "Enterprise") {
      updatedPlans.push({
        title: plan.title,
        description: plan.description,
        priceMonthly: plan.price_monthly,
        priceAnnual: plan.price_annual,
        creditsByDefault: plan.credits_by_default,
        features: plan.features,
        paypalPlanId: {
          monthly: "Enterprise",
          annual: "Enterprise",
        },
      });
    }
  }

  // If we got plans from API, use them; otherwise keep the existing plans as fallback
  if (updatedPlans.length > 0) {
    rawPlansData.value = updatedPlans;
  }
}

function redirectToCheckout(plan) {
  if (isActivePlan(plan.title)) {
    return;
  }

  trackEvent("plan_selected", {
    selected_plan: plan.title,
    current_plan: currentPlan.value,
    billing_cycle: isAnnual.value ? "annual" : "monthly",
    price: isAnnual.value ? plan.priceAnnual : plan.priceMonthly,
    is_upgrade:
      (isAnnual.value ? plan.priceAnnual : plan.priceMonthly) >
      (isAnnual.value
        ? plans[currentPlan.value]?.priceAnnual || 0
        : plans[currentPlan.value]?.priceMonthly || 0),
  });

  if (plan.title.toLowerCase() === "enterprise") {
    sendEnterpriseInquiry();
  } else {
    store.commit("setSelectedPlan", plan);
    store.commit("setIsAnnual", isAnnual.value);
    router.push("/checkout");
  }
}

function sendEnterpriseInquiry() {
  trackEvent("enterprise_inquiry_initiated", {
    current_plan: currentPlan.value,
    billing_cycle: isAnnual.value ? "annual" : "monthly",
  });

  const emailAddress = "sales@avacapo.com";
  const subject = encodeURIComponent("Enterprise Plan Inquiry");
  const body = encodeURIComponent(
    `Hello,\n\nI am interested in the Enterprise plan.\n\nPlease provide more details.\n\nThank you!`
  );

  const mailtoLink = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
  window.location.href = mailtoLink;
}
</script>

<style scoped>
.plan-heading {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: var(--spacing-3xl);
}

.header-1 {
  margin-bottom: 5%;
}

.plan-content {
  display: flex;
  width: 90%;
  justify-content: space-between;
  margin-bottom: var(--spacing-3xl);
}

.plan-card {
  width: 20%;
}

.toggle-container {
  display: inline-flex;
  width: 60%;
  justify-content: space-between;
  padding: 2px;
  background-color: var(--bg-card);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  margin-bottom: 10%;
}

.toggle-option {
  width: 4.6rem;
  text-align: center;
  padding: 5px 10px;
  font-size: var(--font-size-description);
  color: var(--color-white);
  background-color: var(--bg-card);
  border-radius: var(--border-radius-lg);
  transition-duration: var(--transition-slow);
}

.toggle-option.active {
  color: var(--color-black);
  background-color: var(--color-white-70);
}

.plan-content-description {
  text-align: center;
  margin-bottom: var(--spacing-3xl);
}

@media (max-width: 970px) {
  .plan-content {
    flex-direction: column;
    align-items: center;
  }

  .plan-card {
    width: 90%;
    margin-bottom: var(--spacing-xl);
  }
}
</style>
